import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sunrise,
    Users,
    GraduationCap,
    Compass,
    Shield,
    Handshake,
    Trophy,
    Target,
    Lightbulb,
    ArrowRight,
    Clock,
    BookOpen,
    Hammer,
    HeartHandshake,
} from "lucide-react";
import { IMG, BTN } from "@/lib/design";

const fade = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const PROGRAM = [
    {
        icon: HeartHandshake,
        title: "Therapeutic Support",
        body: "Access to therapy, psychiatric services, medication management, emotional regulation support, and individualized behavioral care designed to help young men heal and stabilize.",
    },
    {
        icon: Users,
        title: "Relationship-Based Care",
        body: "We believe meaningful change happens through trust, consistency, healthy boundaries, and supportive relationships with emotionally regulated adults.",
    },
    {
        icon: Shield,
        title: "Structure & Accountability",
        body: "Young men thrive in environments with clear expectations, daily routines, personal responsibility, and accountability delivered with dignity and respect.",
    },
    {
        icon: GraduationCap,
        title: "Education Coordination",
        body: "We work closely with schools, educational partners, and support teams to promote academic engagement, stability, and educational progress.",
    },
    {
        icon: Hammer,
        title: "Life Skills Development",
        body: "Youth are supported in building practical skills including communication, hygiene, emotional regulation, conflict resolution, decision-making, and independent living preparation.",
    },
    {
        icon: Compass,
        title: "Stability & Growth",
        body: "Our program provides a safe and structured environment where young men can focus on healing, personal growth, emotional wellness, and building healthier futures.",
    },
];

const SCHEDULE = [
    { time: "6:45 AM", title: "Wake & morning routine", desc: "Hygiene, room reset, calm start with breakfast." },
    { time: "8:00 AM", title: "School / academics", desc: "Coordination with local schools and on-site support." },
    { time: "12:30 PM", title: "Lunch & reflection", desc: "Family-style meals, journaling or rest." },
    { time: "2:00 PM", title: "Mentor check-ins", desc: "One-on-one conversations, goal-setting, life skills practice." },
    { time: "4:30 PM", title: "Recreation & life skills", desc: "Sports, cooking, financial literacy and leadership rotations." },
    { time: "6:30 PM", title: "Dinner & community", desc: "Shared meals and house meeting." },
    { time: "8:30 PM", title: "Wind-down & lights out", desc: "Reflection, reading and predictable bedtime." },
];

export default function OurProgram() {
    return (
        <div data-testid="program-page">
            {/* HERO */}
            <section className="bg-[#F7F5F0] pt-16 md:pt-24 pb-12">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Our Program
                    </p>
                    <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        Trauma-informed care grounded in structure, accountability, and connection.
                    </h1>
                    <p className="mt-6 text-[17px] leading-relaxed text-[#6F6B63] max-w-3xl">
                        Crossroads to Change Residential Treatment Center
                        combines therapeutic support, education coordination,
                        life skills development, and relationship-based care
                        within a safe and structured environment designed to
                        help young men heal, grow, and stabilize.
                    </p>
                    <p className="mt-4 text-[17px] leading-relaxed text-[#6F6B63] max-w-3xl">
                        Our program supports male youth in developing emotional
                        regulation, healthy relationships, personal
                        responsibility, confidence, and a stronger sense of
                        identity.
                    </p>
                </div>
            </section>

            {/* PROGRAM COMPONENTS — de-boxed, clean grid */}
            <section className="py-20 md:py-28 bg-[#F7F5F0]">
                <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
                    <div className="max-w-2xl mb-16">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            Program components
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            Six areas of focused care.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
                        {PROGRAM.map((p, i) => (
                            <motion.div
                                key={p.title}
                                {...fade}
                                transition={{ ...fade.transition, delay: i * 0.04 }}
                                className="flex gap-5"
                                data-testid={`program-card-${i}`}
                            >
                                <div className="shrink-0">
                                    <div className="h-12 w-12 rounded-2xl bg-[#0E2A47]/5 grid place-items-center text-[#0E2A47]">
                                        <p.icon className="h-5.5 w-5.5" />
                                    </div>
                                </div>
                                <div className="pt-1">
                                    <h3 className="font-[Outfit] text-[20px] font-semibold tracking-tight text-[#1A2230]">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] leading-relaxed text-[#6F6B63]">
                                        {p.body}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STRUCTURE.SUPPORT.ACCOUNTABILITY band */}
            <section className="py-20 md:py-24 bg-[#0E2A47] text-white">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14 text-center">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        What this looks like in practice
                    </p>
                    <h2 className="mt-3 font-[Outfit] text-3xl md:text-5xl font-semibold tracking-tight text-white">
                        Structure. Support. Accountability.
                    </h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
                            <Clock className="h-6 w-6 text-[#C8A24A] mb-4" />
                            <h3 className="font-[Outfit] text-[20px] font-semibold mb-2">Structure</h3>
                            <p className="text-[14.5px] text-white/75 leading-relaxed">
                                Predictable routines, clear expectations, and
                                a calm home-like environment that lets young
                                men stop bracing for what's next.
                            </p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
                            <HeartHandshake className="h-6 w-6 text-[#C8A24A] mb-4" />
                            <h3 className="font-[Outfit] text-[20px] font-semibold mb-2">Therapeutic Support</h3>
                            <p className="text-[14.5px] text-white/75 leading-relaxed">
                                Individual therapy, group support, psychiatric
                                care coordination, medication monitoring, and
                                crisis stabilization — integrated into daily
                                life.
                            </p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
                            <Trophy className="h-6 w-6 text-[#C8A24A] mb-4" />
                            <h3 className="font-[Outfit] text-[20px] font-semibold mb-2">Accountability</h3>
                            <p className="text-[14.5px] text-white/75 leading-relaxed">
                                Honest feedback, clear expectations, and
                                follow-through delivered with dignity — never
                                shame.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* DAILY SCHEDULE */}
            <section className="py-20 md:py-24 bg-[#F7F5F0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
                    <div className="max-w-3xl mb-12">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            A day at Crossroads to Change Residential Treatment Center
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            Structure that lets growth happen.
                        </h2>
                        <p className="mt-5 text-[16px] leading-relaxed text-[#6F6B63]">
                            Every day carries a familiar rhythm. Predictability
                            is one of the most powerful things we can offer a
                            young man.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-7 space-y-3" data-testid="schedule-timeline">
                            {SCHEDULE.map((s, i) => (
                                <motion.div
                                    key={s.time}
                                    {...fade}
                                    transition={{ ...fade.transition, delay: i * 0.04 }}
                                    className="flex items-start gap-5 p-5 rounded-2xl bg-white border border-[#E5DFD3]"
                                >
                                    <span className="font-[Outfit] text-[14px] font-semibold text-[#0E2A47] min-w-[88px] mt-0.5">
                                        {s.time}
                                    </span>
                                    <div>
                                        <p className="font-[Outfit] text-[16px] font-semibold mb-1">
                                            {s.title}
                                        </p>
                                        <p className="text-[14px] text-[#6F6B63]">{s.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="md:col-span-5">
                            <div className="rounded-3xl overflow-hidden aspect-[4/5] sticky top-28">
                                <img
                                    src={IMG.group}
                                    alt="Young men in a positive group setting"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LICENSING NOTE */}
            <section className="py-16 bg-[#EFEBE0]">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="bg-white rounded-2xl border border-[#E5DFD3] p-7">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium mb-2">
                            Licensing & care framework
                        </p>
                        <p className="text-[15px] text-[#1A2230]/85 leading-relaxed">
                            Crossroads to Change Residential Treatment Center
                            is a licensed residential treatment program in
                            Texas. Clinical and psychiatric services are
                            provided through licensed providers. Our care
                            framework is{" "}
                            <strong>trauma-informed</strong> and{" "}
                            <strong>TBRI® (Trust-Based Relational Intervention)</strong>-informed.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-[#F7F5F0]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                        Ready to take the next step?
                    </h2>
                    <p className="mt-4 text-[16px] text-[#6F6B63]">
                        Our team will explain eligibility, timing and the next steps.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/referrals" className={BTN.primary} data-testid="program-refer-cta">
                            Make a referral <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link to="/who-we-serve" className={BTN.outline} data-testid="program-who-cta">
                            Who we serve
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
